/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  BanknotesIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType, SVGProps } from "react";

export type FormTabId =
  | "basics"
  | "content"
  | "licences"
  | "design"
  | "financial";

export interface FormTabDef {
  id: FormTabId;
  label: string;
  hint: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  fields: string[];
}

export const FORM_TABS: FormTabDef[] = [
  {
    id: "basics",
    label: "المعلومات الأساسية",
    hint: "اسم المشروع، التصنيف، الإعدادات والخيارات",
    icon: InformationCircleIcon,
    fields: [
      "name",
      "title",
      "summary",
      "shortDescription",
      "classificationIds",
      "projectType",
      "minimumOpenDonationAmount",
      "image",
      "imageAlt",
      "allRequiredAmount",
      "currentPaidAmount",
      "giftProjectId",
      "order",
      "startsFrom",
      "endsTo",
      "personEmailToNotify",
      "personPhoneToNotify",
      "projectAttributes",
    ],
  },
  {
    id: "content",
    label: "المحتوى والوصف",
    hint: "تفاصيل المشروع وإعدادات SEO",
    icon: DocumentTextIcon,
    fields: [
      "projectDetails",
      "projectDescriptionImages",
      "seoTitle",
      "seoDescription",
      "seoKeywords",
      "slug",
    ],
  },
  {
    id: "licences",
    label: "التراخيص والتوثيقات",
    hint: "ترخيص المشروع ومراحل التوثيق",
    icon: ShieldCheckIcon,
    fields: [
      "projectLicence",
      "projectLicenceCodes",
      "projectVerificationLimitations",
    ],
  },
  {
    id: "design",
    label: "الشهادات والقوالب",
    hint: "تصميم شهادة الوقف واختيار القالب",
    icon: SparklesIcon,
    fields: [
      "certificate",
      "Certificate",
      "endowmentCertificateDesign",
      "themeId",
    ],
  },
  {
    id: "financial",
    label: "الحسابات والأضاحي",
    hint: "الحسابات البنكية وإعدادات الأضاحي",
    icon: BanknotesIcon,
    fields: ["bankAccountsIds", "adahiProductId", "adahiRitualId"],
  },
];

const countErrors = (errors: any, fields: string[]): number =>
  fields.reduce((acc, key) => (errors?.[key] ? acc + 1 : acc), 0);

interface FormTabsProps {
  active: FormTabId;
  onChange: (id: FormTabId) => void;
  errors: any;
}

export const FormTabs = ({ active, onChange, errors }: FormTabsProps) => {
  return (
    <nav className="sticky top-0 z-30 mb-5">
      <div
        role="tablist"
        className="relative flex flex-nowrap items-center gap-1 overflow-x-auto whitespace-nowrap rounded-2xl border border-gray-100 bg-gradient-to-l from-purple-50/30 via-white to-purple-50/20 p-1.5 backdrop-blur-sm [-ms-overflow-style:none] [scrollbar-width:none] dark:border-gray-700/60 dark:from-purple-950/15 dark:via-gray-800 dark:to-purple-950/10 [&::-webkit-scrollbar]:hidden"
      >
        {FORM_TABS.map((tab, idx) => {
          const Icon = tab.icon;
          const isActive = tab.id === active;
          const errorCount = countErrors(errors, tab.fields);
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(tab.id)}
              className={`group/tab relative inline-flex shrink-0 items-center gap-2 rounded-2xl px-3 py-2.5 text-sm font-medium tracking-tight transition-all duration-200 md:px-4 ${
                isActive
                  ? "bg-gradient-to-br from-purple-800 to-purple-950 text-white shadow-md shadow-purple-900/25 ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800"
                  : "text-gray-600 hover:bg-white hover:text-purple-900 hover:shadow-sm dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-purple-300"
              }`}
            >
              <span
                className={`text-[11px] font-bold tabular-nums tracking-wider ${
                  isActive
                    ? "text-white/70"
                    : "text-gray-400 dark:text-gray-500"
                }`}
              >
                {String(idx + 1).padStart(2, "0")}
              </span>
              <Icon
                className={`h-4 w-4 shrink-0 transition-transform ${
                  isActive ? "" : "group-hover/tab:scale-110"
                }`}
              />
              <span className="flex flex-col items-start leading-tight">
                <span>{tab.label}</span>
                <span
                  className={`hidden text-[11px] font-normal md:inline ${
                    isActive
                      ? "text-white/70"
                      : "text-gray-400 dark:text-gray-500"
                  }`}
                >
                  {tab.hint}
                </span>
              </span>
              {errorCount > 0 && (
                <span
                  aria-label={`${errorCount} خطأ`}
                  className={`ms-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums ring-1 ${
                    isActive
                      ? "bg-rose-50 text-rose-700 ring-rose-200/80"
                      : "bg-red-100 text-red-700 ring-red-200 dark:bg-red-900/40 dark:text-red-300 dark:ring-red-800"
                  }`}
                >
                  {errorCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export const findFirstErrorTab = (errors: any): FormTabId | null => {
  if (!errors || Object.keys(errors).length === 0) return null;
  for (const tab of FORM_TABS) {
    if (countErrors(errors, tab.fields) > 0) return tab.id;
  }
  return null;
};
